// Shared building blocks for the marketing pages (/, /what-is, /who-its-for,
// /how-different). Copy lives in the pages; these only carry layout and the
// brand treatment so every page reads as one site.
// Copy source: docs/brand/landing-copy-v3.md and landing-copy-v2-inner-pages.md.

import React from 'react';
import { Link } from 'react-router-dom';
import SignupLink from '../Auth/SignupLink';

export const BTN_PRIMARY = 'inline-flex items-center justify-center px-8 py-4 bg-finance-blue text-white font-bold rounded-lg hover:bg-finance-blue-600 transition-colors';
export const BTN_ON_BLUE = 'inline-flex items-center justify-center px-8 py-4 bg-white text-finance-blue font-bold rounded-lg hover:bg-mist transition-colors';
export const BTN_GHOST_ON_BLUE = 'inline-flex items-center justify-center px-8 py-4 text-white font-bold rounded-lg border border-white/40 hover:bg-white/10 transition-colors';

// Section grounds. Every section picks one so text colours follow.
const GROUND = {
  white: { section: 'bg-white dark:bg-midnight', title: 'text-midnight dark:text-white', body: 'text-midnight/75 dark:text-white/75', eyebrow: 'text-finance-blue' },
  mist: { section: 'bg-mist dark:bg-midnight-800', title: 'text-midnight dark:text-white', body: 'text-midnight/75 dark:text-white/75', eyebrow: 'text-finance-blue' },
  blue: { section: 'bg-finance-blue text-white', title: 'text-white', body: 'text-white/85', eyebrow: 'text-white/75' },
  midnight: { section: 'bg-midnight text-white', title: 'text-white', body: 'text-white/80', eyebrow: 'text-finance-blue-300' },
};

export const groundText = (ground) => GROUND[ground] || GROUND.white;

export const Section = ({ ground = 'white', className = 'py-20', id, labelledBy, children }) => (
  <section id={id} aria-labelledby={labelledBy} className={`${groundText(ground).section} ${className}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
  </section>
);

export const SectionHead = ({ ground = 'white', eyebrow, title, id, children, className = 'mb-12', as: Tag = 'h2' }) => {
  const g = groundText(ground);
  return (
    <div className={`max-w-3xl ${className}`}>
      {eyebrow ? <p className={`text-eyebrow ${g.eyebrow} mb-3`}>{eyebrow}</p> : null}
      <Tag id={id} className={`${Tag === 'h1' ? 'text-display-sm sm:text-display' : 'text-heading sm:text-display-sm'} ${g.title}`}>{title}</Tag>
      {children ? <div className={`mt-4 text-body space-y-4 ${g.body}`}>{children}</div> : null}
    </div>
  );
};

// A mist card on white, a white card on mist.
export const Card = ({ on = 'white', className = '', children }) => (
  <div className={`rounded-2xl p-6 ${on === 'mist' ? 'bg-white dark:bg-midnight-700' : 'bg-mist dark:bg-midnight-800'} ${className}`}>{children}</div>
);

// The inner box of a mock (the product talking).
export const Note = ({ on = 'mist', label, children, className = '' }) => (
  <div className={`rounded-xl px-4 py-3 text-sm leading-relaxed ${on === 'mist' ? 'bg-white dark:bg-midnight-700' : 'bg-mist dark:bg-midnight-800'} text-midnight dark:text-white ${className}`}>
    {label ? <p className="text-xs font-bold text-finance-blue mb-1">{label}</p> : null}
    {children}
  </div>
);

// Looks like a button inside a product mock; not interactive.
export const MockButton = ({ primary, children }) => (
  <span className={`inline-flex items-center min-h-[40px] px-4 rounded-lg text-sm font-bold ${primary ? 'bg-finance-blue text-white' : 'bg-white dark:bg-midnight border border-mist-300 dark:border-midnight-600 text-midnight dark:text-white'}`}>{children}</span>
);

/**
 * The Home hero's chart, drawn static: the month's forecast as a line that
 * moves day by day, spending so far climbing toward it, and a dashed
 * projection from today's spend to the forecast at month end. No plan line.
 */
export const ForecastPanel = ({ month = 'November', total = '$6,240', spent = '$3,480', buffer = '$610', compact = false }) => (
  <div className="rounded-xl bg-finance-blue text-white p-4">
    <p className="text-xs font-bold text-white/85">{month}</p>
    <p className="mt-1 text-3xl font-extrabold tracking-tight tabular-nums">{total}</p>
    <p className="text-sm text-white/90">forecast for the month, as of today</p>
    <svg
      className={`mt-2 w-full ${compact ? 'h-28' : 'h-36'}`}
      viewBox="0 0 400 150"
      role="img"
      aria-label={`The forecast for ${month} has moved day by day and sits at ${total} today. Spending so far is ${spent} and is projected to reach the forecast by the end of the month.`}
    >
      <line x1="36" y1="28" x2="392" y2="28" stroke="rgba(255,255,255,0.12)" />
      <line x1="36" y1="79" x2="392" y2="79" stroke="rgba(255,255,255,0.12)" />
      <line x1="36" y1="130" x2="392" y2="130" stroke="rgba(255,255,255,0.12)" />
      <text x="30" y="32" textAnchor="end" fontSize="10" fill="#FFFFFF" fillOpacity="0.8">$7K</text>
      <text x="30" y="83" textAnchor="end" fontSize="10" fill="#FFFFFF" fillOpacity="0.8">$3.5K</text>
      <text x="30" y="134" textAnchor="end" fontSize="10" fill="#FFFFFF" fillOpacity="0.8">$0</text>
      <path d="M36 37 L62 41 L88 35 L114 39 L140 43 L166 36 L192 40 L214 42 L238 39" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M238 39 L392 39" fill="none" stroke="#10B981" strokeOpacity="0.45" strokeWidth="2" strokeDasharray="2 5" />
      <text x="42" y="22" fontSize="10" fill="#10B981" fontWeight="700">Forecast for {month}</text>
      <path d="M36 130 C70 124 96 118 118 112 C150 103 176 92 200 86 C214 82 226 80 238 79" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" />
      <path d="M238 79 C280 72 330 58 360 50 C375 46 386 42 392 39" fill="none" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeDasharray="6 6" />
      <circle cx="238" cy="79" r="4" fill="#FFFFFF" />
      <text x="232" y="98" textAnchor="end" fontSize="10" fill="#FFFFFF" fontWeight="700">Spent so far {spent}</text>
      <text x="36" y="146" fontSize="10" fill="#FFFFFF">1</text>
      <text x="238" y="146" textAnchor="middle" fontSize="10" fill="#FFFFFF">Today</text>
      <text x="392" y="146" textAnchor="end" fontSize="10" fill="#FFFFFF">30</text>
    </svg>
    <p className="text-xs text-white/85 tabular-nums">Remaining buffer this month {buffer}</p>
  </div>
);

// A small history-to-forecast line for cards that only need the gesture.
export const MiniForecastLine = () => (
  <svg className="w-full h-16" viewBox="0 0 280 72" aria-hidden="true">
    <line x1="0" y1="60" x2="280" y2="60" stroke="#D5DDFB" strokeWidth="1" />
    <path d="M0 58 C40 56 70 44 110 40 C130 38 145 36 160 34" fill="none" stroke="#3361FF" strokeWidth="3" strokeLinecap="round" />
    <path d="M160 34 C190 30 230 22 280 12" fill="none" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeDasharray="6 5" />
    <circle cx="160" cy="34" r="4" fill="#0B1533" />
  </svg>
);

// A chat exchange for the Ask sections.
export const AskExchange = ({ question, answer }) => (
  <div className="rounded-2xl bg-mist dark:bg-midnight-800 p-5 flex flex-col gap-3" aria-label="Example question and answer">
    <p className="self-end max-w-[85%] rounded-2xl rounded-br-md bg-finance-blue text-white px-4 py-3 text-[15px] leading-relaxed">{question}</p>
    <p className="self-start max-w-[90%] rounded-2xl rounded-bl-md bg-white dark:bg-midnight-700 text-midnight dark:text-white px-4 py-3 text-[15px] leading-relaxed">{answer}</p>
  </div>
);

// Closing call to action on Finance Blue.
export const ClosingCTA = ({ title, body, secondary }) => (
  <Section ground="blue" className="py-20" labelledBy="closing-cta">
    <div className="max-w-3xl mx-auto text-center">
      <h2 id="closing-cta" className="text-heading sm:text-display-sm">{title}</h2>
      <p className="mt-4 text-body text-white/85">{body}</p>
      <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
        <SignupLink className={BTN_ON_BLUE} closedLabel="Join the waitlist">Get started</SignupLink>
        {secondary ? <Link to={secondary.to} className={BTN_GHOST_ON_BLUE}>{secondary.label}</Link> : null}
      </div>
    </div>
  </Section>
);
